import type { SkillConfig } from "@/generated";

export const SKILL_MD = "SKILL.md";

export const EMPTY_SKILL = (): SkillConfig => ({
  name: "",
  usage_conditions: "",
  tags: [],
  version: "1.0.0",
  author: "",
  instructions: "",
  enabled: true,
  resources: {},
});

export interface ParsedSkillMd {
  frontmatter: Record<string, string | string[]>;
  instructions: string;
}

// Only a leading `---` block counts, so a horizontal rule further down the
// document is never mistaken for frontmatter.
const FRONTMATTER = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*\r?\n?/;

const unquote = (value: string): string => {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    // One pass, so an escaped backslash before an `n` stays a backslash.
    return value
      .slice(1, -1)
      .replace(/\\(.)/g, (_, ch) =>
        ch === "n" ? "\n" : ch === "r" ? "\r" : ch
      );
  }
  if (value.length >= 2 && value.startsWith("'") && value.endsWith("'")) {
    return value.slice(1, -1);
  }
  return value;
};

export function parseSkillMd(content: string): ParsedSkillMd {
  const match = content.match(FRONTMATTER);
  if (!match) return { frontmatter: {}, instructions: content.trim() };

  const instructions = content.slice(match[0].length).trim();
  const frontmatter: Record<string, string | string[]> = {};
  let currentKey: string | null = null;
  let currentList: string[] | null = null;

  for (const rawLine of match[1].split("\n")) {
    const line = rawLine.trimEnd();
    if (!line) continue;

    const listItem = line.match(/^[ \t]+-[ \t]+(.*)$/);
    if (listItem && currentKey) {
      if (!currentList) {
        currentList = [];
        frontmatter[currentKey] = currentList;
      }
      currentList.push(unquote(listItem[1].trim()));
      continue;
    }

    const colonIdx = line.indexOf(":");
    if (colonIdx === -1) continue;

    currentKey = line.slice(0, colonIdx).trim();
    currentList = null;
    const value = line.slice(colonIdx + 1).trim();
    // Recorded even when empty: `skillFromParsed` distinguishes "written, and
    // deliberately blank" from "absent", and a list key legitimately has no
    // scalar of its own (`tags:` on its own line).
    if (!value && frontmatter[currentKey] !== undefined) continue;
    frontmatter[currentKey] = value ? unquote(value) : "";
  }

  return { frontmatter, instructions };
}

const asString = (value: string | string[] | undefined): string =>
  typeof value === "string" ? value : "";

export function skillFromParsed(
  parsed: ParsedSkillMd,
  resources: Record<string, string>,
  folderName?: string
): SkillConfig {
  const fm = parsed.frontmatter;
  const name = asString(fm.name) || folderName || "";

  // `description` is what SKILL.md frontmatter is called everywhere else, so a
  // skill authored outside Noxus still imports with its usage conditions. Our
  // key wins whenever it is present, blank included: a re-export that carries
  // both and was deliberately emptied must not silently pick the stale one up
  // again.
  let usageConditions =
    fm.usage_conditions !== undefined
      ? asString(fm.usage_conditions)
      : asString(fm.description);
  if (!usageConditions && fm.usage_conditions === undefined) {
    usageConditions =
      parsed.instructions
        .split("\n")
        .map((line) => line.trim())
        .find((line) => line && !line.startsWith("#")) ?? "";
  }

  return {
    name,
    usage_conditions: usageConditions,
    tags: Array.isArray(fm.tags) ? fm.tags : [],
    version: asString(fm.version) || "1.0.0",
    author: asString(fm.author),
    instructions: parsed.instructions,
    enabled: true,
    resources,
  };
}

// A bare scalar is ambiguous to a real YAML parser when it carries a `: `,
// leads with an indicator character, or has edge whitespace. Our own parser
// splits on the first colon and would cope, but exported ZIPs are read by
// other tools too.
//
// A newline is the dangerous one: the Usage conditions field is a rich text
// editor, so it takes one keypress, and an unquoted multi-line scalar splits
// the document.
// The parser reads frontmatter a line at a time, so the value would truncate
// at its first line and any later `k: v` line inside it would become a key --
// usage conditions containing `name:` could rename the skill on re-import.
const needsQuoting = (value: string): boolean =>
  /[\n\r]/.test(value) ||
  /: /.test(value) ||
  /^[-?:,[\]{}#&*!|>'"%@`]/.test(value) ||
  value !== value.trim();

// Double-quoted style, which is the one YAML lets you write `\n` in.
const yamlScalar = (value: string): string =>
  needsQuoting(value)
    ? `"${value
        .replace(/([\\"])/g, "\\$1")
        .replace(/\n/g, "\\n")
        .replace(/\r/g, "\\r")}"`
    : value;

export function buildSkillMd(skill: SkillConfig): string {
  const lines = [`name: ${yamlScalar(skill.name)}`];

  // Written under the ecosystem's key, not ours: an exported ZIP is read by
  // other tools, and `skillFromParsed` accepts either name on the way back in.
  if (skill.usage_conditions) {
    lines.push(`description: ${yamlScalar(skill.usage_conditions)}`);
  }
  if (skill.version) lines.push(`version: ${yamlScalar(skill.version)}`);
  if (skill.author) lines.push(`author: ${yamlScalar(skill.author)}`);
  if (skill.tags?.length) {
    lines.push("tags:");
    for (const tag of skill.tags) lines.push(`  - ${yamlScalar(tag)}`);
  }

  return `---\n${lines.join("\n")}\n---\n\n${skill.instructions ?? ""}`;
}
